import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { settingsRepository } from '@/data/settings/repositories/settings_repository_impl'
import { getAxiosLikeErrorMessage } from '@/services/apiErrors'
import { logError } from '@/utils/logger'
import type { SupportFormErrors } from '../models/settings_ui_state'

export function useSupportSettingsViewModel() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [mobileNumber, setMobileNumber] = useState('')
  const [errors, setErrors] = useState<SupportFormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      try {
        const data = await settingsRepository.getSupport()
        if (!cancelled) {
          setEmail(data.email)
          setMobileNumber(data.mobileNumber)
        }
      } catch (error) {
        logError('Failed to load support information:', error)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setIsSubmitting(true)

      const newErrors: SupportFormErrors = {}
      if (!email.trim()) {
        newErrors.email = 'Email is required'
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        newErrors.email = 'Please enter a valid email address'
      }
      if (!mobileNumber.trim()) {
        newErrors.mobileNumber = 'Mobile number is required'
      } else if (!/^[0-9+\-\s()]+$/.test(mobileNumber.trim())) {
        newErrors.mobileNumber = 'Please enter a valid mobile number'
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors)
        setIsSubmitting(false)
        return
      }

      try {
        await settingsRepository.saveSupport({ email: email.trim(), mobileNumber: mobileNumber.trim() })
        alert('Support information saved successfully!')
      } catch (error: unknown) {
        logError('Failed to save support information:', error)
        alert(getAxiosLikeErrorMessage(error, 'Failed to save support information. Please try again.'))
      } finally {
        setIsSubmitting(false)
      }
    },
    [email, mobileNumber]
  )

  const handleCancel = useCallback(() => {
    navigate('/admin')
  }, [navigate])

  return {
    email,
    mobileNumber,
    errors,
    isSubmitting,
    isLoading,
    setEmail,
    setMobileNumber,
    setErrors,
    handleSubmit,
    handleCancel,
  }
}
